import {
  Component,
  HostListener,
  OnInit,
  ViewChild,
  AfterViewInit,
  OnDestroy
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { VideosAndEventsService } from 'src/app/services/videos-and-events/videos-and-events.service';
import { ICategoryModel } from 'src/app/services/videos-and-events/models/category-model';
import { IVideoResponseModel } from 'src/app/services/videos-and-events/models/videos-and-events-model';
import { VIDEOS_AND_EVENTS_CATEGORIES } from '../../shared/enums/videos-and-events.enum';
import {
  debounceTime,
  distinctUntilChanged,
  Subject,
  Subscription
} from 'rxjs';
import { IFilterModel } from './interfaces/search';
import { IVideo } from './interfaces/videosByCategory';
import { CardListBreakpointsService } from '../../videos-and-events-category/services/card-list-breakpoints.service';
import { LiveTransmissionsComponent } from './components/live-transmissions/live-transmissions.component';
import { VideoModalHandlerService } from './services/video-modal-handler/video-modal-handler.service';

@Component({
  selector: 'app-videos-and-events',
  templateUrl: './videos-and-events.component.html',
  styleUrls: ['./videos-and-events.component.scss']
})
export class VideosAndEventsComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  liveTransmissionCategoryId = VIDEOS_AND_EVENTS_CATEGORIES.LIVE_TRANSMISSIONS;
  @ViewChild(LiveTransmissionsComponent)
  liveTransmissionsComponent: LiveTransmissionsComponent;
  isLoadingCategories = false;
  categories: ICategoryModel[] = [];
  totalCountCategories: number;
  currentPageCategories = 0;
  pageSizeCategories = 3;
  isLoading = false;
  searchString: string = '';
  currentFilter: IFilterModel = {
    search: '',
    pageNumber: 0,
    pageSize: 10,
    tag: []
  };
  videos: IVideo[] = [];
  totalCount: number;
  currentPage = 0;
  pageSize = 12;
  isSearching = false;
  emptyVideoCards: null[] = [];
  isSearchLoading = false;
  searchTotalCount: number;
  searchCurrentPage = 0;
  searchPageSize = 10;

  liveTransmissionCategoryItems: IVideo[];
  showLiveTransmissionsCategory: boolean;

  private subscription: Subscription = new Subscription();

  private searchInputSubject = new Subject<string>();

  isMobile = false;

  constructor(
    private videosAndEventsService: VideosAndEventsService,
    private cardListBreakpointsService: CardListBreakpointsService,
    private route: ActivatedRoute,
    private router: Router,
    private videoModalHandlerService: VideoModalHandlerService
  ) {}

  ngOnInit(): void {
    this.setIsMobileSize();
    window.addEventListener('resize', this.setIsMobileSize.bind(this));
    this.checkQueryParams();
    this.loadCategoriesPerPage();

    this.searchInputSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchText) => {
        this.loadVideosPerSearch(searchText);
      });
  }

  private checkQueryParams(): void {
    this.route.queryParams.subscribe((params) => {
      const eventParam = params['event'];
      const videoParam = params['video'];
      if (eventParam) this.loadAndOpenVideoOrEvent(eventParam, 'event');
      else if (videoParam) this.loadAndOpenVideoOrEvent(videoParam, 'video');
    });
  }

  private loadAndOpenVideoOrEvent(
    id: string,
    contentType: 'video' | 'event'
  ): void {
    this.videosAndEventsService.getVideoOrEvent(id).subscribe({
      next: (response) => {
        const video = this.mapVideoResponseToIVideo(response);
        const isLive = response.statusTransmissaoValue === 'ao_vivo';
        this.videoModalHandlerService.openVideoModal(video, isLive);
      }
    });
  }

  private mapVideoResponseToIVideo(videoResponse: IVideoResponseModel): IVideo {
    return {
      id: videoResponse.id,
      title: videoResponse.titulo,
      subtitle: videoResponse.resumo,
      videoURL: videoResponse.conteudo,
      categoryId: videoResponse.categoria.id,
      imagem: videoResponse.imagem,
      imagemMobile: videoResponse.imagemMobile,
      data: videoResponse.data,
      statusTransmissaoValue: videoResponse.statusTransmissaoValue,
      dateTimeTransmissao: videoResponse.dateTimeTransmissao
    };
  }

  private setIsMobileSize(): void {
    this.isMobile = window.innerWidth <= 900;
    if (this.isMobile) {
      this.pageSize = 4;
    } else {
      this.getPageSize();
    }
  }

  ngAfterViewInit(): void {
    if (!this.liveTransmissionsComponent) return;

    this.subscription.add(
      this.liveTransmissionsComponent.liveTransmissionCategoryItemsChange$.subscribe(
        (liveTransmissionCategoryItems: IVideo[]) => {
          setTimeout(() => {
            this.liveTransmissionCategoryItems = liveTransmissionCategoryItems;
            this.showLiveTransmissionsCategory =
              !!liveTransmissionCategoryItems.length;
            this.loadCategoriesPerPage();
          });
        }
      )
    );
  }

  loadCategoriesPerPage(): void {
    this.isLoadingCategories = true;
    this.videosAndEventsService
      .getPagedCategories({
        page: this.currentPageCategories + 1,
        pageSize: this.pageSizeCategories,
        ...(!this.showLiveTransmissionsCategory && {
          ignoreIdCategory: this.liveTransmissionCategoryId
        })
      })
      .result$.subscribe({
        next: (response) => {
          const data = response?.data;
          if (!data) return;

          this.categories = data.items;
          this.pageSizeCategories = data.pageSize;
          this.totalCountCategories = data.totalCount;

          this.isLoadingCategories = false;
        },
        error: (error) => {
          console.error('Failed to load categories', error);
          this.isLoadingCategories = false;
        }
      });
  }

  loadVideosPerSearch(searchString: string = ''): void {
    this.isSearchLoading = true;
    this.isSearching = !!searchString;

    const currentScrollPosition = window.scrollY;

    this.videosAndEventsService
      .getVideosWithQuery({
        page: this.searchCurrentPage + 1,
        pageSize: this.searchPageSize,
        title: searchString
      })
      .result$.subscribe({
        next: (response) => {
          const data = response?.data;
          if (!data) return;

          this.videos = data.items.map((video) => ({
            id: video.id,
            title: video.titulo,
            subtitle: video.resumo,
            videoURL: video.conteudo,
            categoryId: video.categoria.id,
            imagem: video.imagem,
            imagemMobile: video.imagemMobile,
            data: video.data,
            statusTransmissaoValue: video.statusTransmissaoValue,
            dateTimeTransmissao: video.dateTimeTransmissao
          }));
          this.searchPageSize = data.pageSize;
          this.searchTotalCount = data.totalCount;
          this.getEmptyVideoCards();
          this.isSearchLoading = false;

          window.scrollTo(0, currentScrollPosition);
        },
        error: (error) => {
          console.error('Failed to load videos', error);
          this.isSearchLoading = false;
        }
      });
  }

  onPageChange(page: number): void {
    if (page === this.currentPageCategories + 1) return;
    this.scrollToTop();
    this.currentPageCategories = page - 1;
    this.loadCategoriesPerPage();
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  checkIfMainCarouselIsLoading(): boolean {
    return !!this.liveTransmissionsComponent?.isLoading;
  }

  onSearchInputChange(event: Event): void {
    this.isSearchLoading = true;
    const inputElement = event.target as HTMLInputElement;
    this.searchString = inputElement.value;
    this.searchInputSubject.next(this.searchString);
  }

  onSearchPageChange(page: number): void {
    if (page === this.searchCurrentPage + 1) return;
    this.scrollToTop();
    this.searchCurrentPage = page - 1;
    this.loadVideosPerSearch(this.searchString);
  }

  getEmptyVideoCards() {
    this.emptyVideoCards = this.cardListBreakpointsService.getEmptyVideoCards(
      this.videos.length
    );
  }

  getPageSize() {
    const itemsPerRow = this.cardListBreakpointsService.getItemsPerRow(
      window.innerWidth
    );
    this.searchPageSize = itemsPerRow * 3;
  }

  @HostListener('window:resize')
  onResize() {
    this.getEmptyVideoCards();
    this.getPageSize();
    this.loadVideosPerSearch(this.searchString);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
